/**
 * Single source of truth for application navigation.
 *
 * Both the desktop sidebar (ModernSidebar) and the mobile bottom nav
 * (MobileBottomNav) render from this list, so labels, icons and routes can no
 * longer drift apart between the two.
 *
 * - `icon` is the Font Awesome name WITHOUT the `fas` prefix (callers add it).
 * - `mobilePrimary` marks the (up to 4) items shown as tabs in the mobile
 *   bottom bar; every other item appears in the "Więcej" sheet.
 */

export const NAV_SECTIONS = [
  {
    id: 'main',
    title: 'Główne',
    items: [
      { path: '/field-seasons', icon: 'fa-seedling', label: 'Uprawy', mobilePrimary: true },
      { path: '/field-treatments', icon: 'fa-tools', label: 'Zabiegi' },
      { path: '/deliveries', icon: 'fa-truck', label: 'Dostawy', mobilePrimary: true },
      { path: '/sales', icon: 'fa-dollar-sign', label: 'Sprzedaż' },
    ],
  },
  {
    id: 'fertilization',
    title: 'Nawożenie',
    items: [
      { path: '/soil-analyses', icon: 'fa-flask', label: 'Badania gleby' },
      { path: '/nutrient-balance', icon: 'fa-balance-scale', label: 'Potrzeby pokarmowe', mobileLabel: 'Potrzeby' },
      { path: '/fertilization-planner', icon: 'fa-seedling', label: 'Planer nawożenia', mobileLabel: 'Planer' },
      { path: '/fertilization-comparison', icon: 'fa-balance-scale', label: 'Porównanie nawożenia', mobileLabel: 'Porównanie' },
      { path: '/spraying-products', icon: 'fa-spray-can', label: 'Środki ochrony roślin', mobileLabel: 'Środki ochrony' },
      { path: '/spraying-register', icon: 'fa-clipboard-list', label: 'Ewidencja zabiegów', mobileLabel: 'Ewidencja' },
      { path: '/fertilizer-stocks', icon: 'fa-boxes-stacked', label: 'Zapasy nawozów' },
    ],
  },
  {
    id: 'analytics',
    title: 'Analityka',
    items: [
      { path: '/stocks', icon: 'fa-warehouse', label: 'Magazyn', mobilePrimary: true },
      { path: '/report', icon: 'fa-chart-line', label: 'Bilans', mobileLabel: 'Raport', mobilePrimary: true },
      { path: '/harvest-quality', icon: 'fa-vial', label: 'Jakość plonu' },
      { path: '/statistics', icon: 'fa-chart-bar', label: 'Statystyki' },
      { path: '/season-prices', icon: 'fa-tags', label: 'Ceny sezonowe' },
    ],
  },
  {
    id: 'machinery',
    title: 'Maszyny',
    items: [
      { path: '/machines', icon: 'fa-tractor', label: 'Flota maszyn', mobileLabel: 'Maszyny' },
      { path: '/parts', icon: 'fa-puzzle-piece', label: 'Katalog części', mobileLabel: 'Części' },
    ],
  },
  {
    id: 'settings',
    title: 'Ustawienia',
    items: [
      { path: '/warehouses', icon: 'fa-warehouse', label: 'Szablony magazynów', mobileLabel: 'Szablony' },
      { path: '/fields', icon: 'fa-map', label: 'Pola' },
      { path: '/trailers', icon: 'fa-trailer', label: 'Przyczepy' },
      { path: '/contractors', icon: 'fa-handshake', label: 'Kontrahenci' },
      { path: '/fertilizer-base', icon: 'fa-flask', label: 'Baza nawozów' },
      { path: '/spraying-base', icon: 'fa-spray-can', label: 'Baza środków ochrony', mobileLabel: 'Baza środków' },
      { path: '/agopengps', icon: 'fa-satellite-dish', label: 'AgOpenGPS' },
      { path: '/passkeys', icon: 'fa-fingerprint', label: 'Klucze dostępu' },
    ],
  },
];

const ALL_ITEMS = NAV_SECTIONS.flatMap((section) => section.items);

/** Up to four items shown as tabs in the mobile bottom bar. */
export const MOBILE_PRIMARY_ITEMS = ALL_ITEMS.filter((item) => item.mobilePrimary);

/**
 * The same items grouped under their section title, so the sheet is scannable
 * rather than one flat list of nineteen links. Sections whose every item is a
 * bottom-bar tab drop out.
 */
export const MOBILE_SECONDARY_SECTIONS = NAV_SECTIONS
  .map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.mobilePrimary)
  }))
  .filter((section) => section.items.length > 0);

/** `/` renders the same page as `/stocks`, so it has to light up the same entry. */
const LANDING_PATH = '/stocks';

const resolvePath = (pathname) => (pathname === '/' ? LANDING_PATH : pathname);

export const isNavItemActive = (item, pathname) => {
  const path = resolvePath(pathname);
  return path === item.path || path.startsWith(`${item.path}/`);
};

/**
 * The nav entry a pathname belongs to, longest match first so `/machines/12`
 * resolves to `/machines` rather than to nothing.
 */
export const findNavItemByPath = (pathname) => [...ALL_ITEMS]
  .sort((a, b) => b.path.length - a.path.length)
  .find((item) => isNavItemActive(item, pathname));
